import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { CardRewardsEarnRates } from '../../types/CardRewardsEarnRates';

const DEFAULT_SPEND: CardRewardsEarnRates = {
  allPurchases: 1700, // General spending outside specific categories
  allForeignPurchases: 0, // Purchases made outside the United States (defaults to zero unless specified)
  gas: 130, // Gasoline for personal vehicles
  grocery: 330, // Groceries, household food
  streaming: 17, // Streaming services (e.g., Netflix, Hulu)
  drugstore: 42, // Over-the-counter medicine, personal care
  office_supplies: 17, // Work-related supplies, home office purchases
  internet_cable_phone: 100, // Combined cost of internet, cable, and phone services
  dining: 250, // Dining out, restaurants, cafes
  'dining.takeout_delivery': 83, // Food delivery and takeout services
  travel: 250, // General travel expenses
  'travel.hotel': 67, // General hotel stays
  'travel.airline': 83, // General airline spending
  'travel.airline.united': 25, // United Airlines (specific airlines optional)
  'travel.airline.delta': 25, // Delta Airlines
  'travel.airline.american': 25, // American Airlines
  'travel.airline.southwest': 25, // Southwest Airlines
  'travel.airline.alaska': 25, // Alaska Airlines
  'travel.airline.aerLingus': 25, // Aer Lingus
  'travel.transit': 50, // Public transit, Uber/Lyft, etc.
  'travel.rideshare': 50, // Rideshare
  'travel.rideshare.lyft': 25, // Lyft
  'travel.rideshare.uber': 25, // Uber
  'travel.travelportal': 50, // Travel portal
};

export const useMonthlySpendState = (
  overrides: Partial<CardRewardsEarnRates> | null,
  setOverrides: (
    overrides:
      | Partial<CardRewardsEarnRates>
      | ((prev: Partial<CardRewardsEarnRates>) => Partial<CardRewardsEarnRates>)
  ) => void
) => {
  const spendWithOverrides = useMemo(() => {
    if (!overrides || Object.keys(overrides).length === 0) {
      return DEFAULT_SPEND;
    }
    const defaultSpend = { ...DEFAULT_SPEND };
    Object.entries(overrides).forEach(([key, value]) => {
      if (key in defaultSpend) {
        defaultSpend[key as keyof CardRewardsEarnRates] = value;
      }
    });

    return defaultSpend;
  }, [overrides]);

  const reset = useCallback(
    (spendCategory?: string) => {
      if (spendCategory) {
        setOverrides((prev: Partial<CardRewardsEarnRates>) => {
          const newOverrides = { ...prev };
          delete newOverrides[spendCategory as keyof CardRewardsEarnRates];
          return newOverrides;
        });
      } else {
        setOverrides({});
      }
    },
    [setOverrides]
  );

  return [spendWithOverrides, setOverrides, reset] as const;
};
